/**
 * Shared sort menu component used by browse and matches views.
 */

export interface SortRow {
  key: string;
  icon: string;
  label: string;
}

/** Render the sort menu dropdown HTML */
export function renderSortMenu(rows: SortRow[][], activeKey: string): string {
  return `
    <div class="seg-rows" id="sort-menu" style="display: none;">
      ${rows.map(row => `
        <div class="seg-row">
          ${row.map(opt => `
            <button class="seg-btn ${opt.key === activeKey ? 'active' : ''}" data-sort="${opt.key}">
              <i class="fas fa-${opt.icon}"></i> ${opt.label}
            </button>
          `).join('')}
        </div>
      `).join('')}
    </div>
  `;
}

/** Look up a sort label by key from a rows structure */
export function getSortLabel(rows: SortRow[][], key: string, fallback = 'Recent'): string {
  for (const row of rows) {
    for (const opt of row) {
      if (opt.key === key) return opt.label;
    }
  }
  return fallback;
}

/**
 * Wire up sort menu toggle, option selection, and close-on-outside-click.
 * `onSelect` is called with the new sort key when the user picks a different option.
 * `otherMenuIds` are additional menus to close when the sort menu opens.
 */
export function setupSortMenuHandlers(
  currentKey: () => string,
  onSelect: (key: string) => void,
  otherMenuIds: string[] = [],
): void {
  // Toggle
  document.getElementById('sort-btn')?.addEventListener('click', () => {
    const menu = document.getElementById('sort-menu')!;
    for (const id of otherMenuIds) {
      const other = document.getElementById(id);
      if (other) other.style.display = 'none';
    }
    menu.style.display = menu.style.display === 'none' ? 'flex' : 'none';
  });

  // Option selection
  document.querySelectorAll('#sort-menu .seg-btn').forEach(option => {
    option.addEventListener('click', () => {
      const newKey = (option as HTMLElement).dataset.sort;
      if (!newKey) {
        document.getElementById('sort-menu')!.style.display = 'none';
        return;
      }

      if (newKey === currentKey()) {
        document.getElementById('sort-menu')!.style.display = 'none';
        return;
      }

      // Update active state
      document.querySelectorAll('#sort-menu .seg-btn').forEach(opt => opt.classList.remove('active'));
      option.classList.add('active');

      // Update label
      const sortLabel = document.getElementById('sort-label');
      if (sortLabel) sortLabel.textContent = option.textContent?.trim() || '';

      // Hide menu
      document.getElementById('sort-menu')!.style.display = 'none';

      onSelect(newKey);
    });
  });

  // Close on outside click
  document.addEventListener('click', (e) => {
    const sortBtn = document.getElementById('sort-btn');
    const sortMenu = document.getElementById('sort-menu');
    if (sortBtn && sortMenu && !sortBtn.contains(e.target as Node) && !sortMenu.contains(e.target as Node)) {
      sortMenu.style.display = 'none';
    }
  });
}
